import { readonly, ref, type DeepReadonly, type Ref } from 'vue'
import { api } from '@/lib/api'

/**
 * Was ein Kundenzugang in SEINEM Projekt darf (15.08.2026).
 *
 * Die Antwort kommt vom Server (`GET /projects/{id}/module` → `meine`) und ist
 * dieselbe Liste, an der dort auch die Grenzen hängen: `view`, `comment`,
 * `edit` sind die Stellungen der Vorschau, `publish` ist das Recht, das
 * Gespeicherte auf die echte Website zu stellen.
 *
 * Warum überhaupt im Panel: damit die Leiste nur zeigt, was er wirklich kann.
 * Ein Knopf, den der Server abweist, ist schlechter als kein Knopf — das war
 * heute schon einmal die Lehre (die roten „HTTP 403"-Zeilen im Kundenlink).
 * Die GRENZE zieht das hier nicht, die zieht der Server.
 *
 * Einmal geladen und gemerkt: Der Router fragt beim ersten Sprung, die Leiste
 * beim Zeichnen, die Hülle beim Bauen ihres Menüs. Drei Anfragen für dieselbe
 * Antwort wären Verschwendung, und zwei davon kämen nach dem ersten Bild.
 */
const rechte = ref<string[] | null>(null)
let fuerProjekt: string | null = null
let laufend: Promise<string[]> | null = null

/**
 * Wer nicht von außen kommt, darf alles — für ihn wird nicht gefragt.
 * Der Vorgabewert deckt außerdem den Fall ab, dass die Antwort ausbleibt:
 * ansehen und kommentieren, mehr nicht.
 */
const VORSICHTIG = ['view', 'comment']

async function laden(projectId: string): Promise<string[]> {
  if (fuerProjekt === projectId && rechte.value !== null) return rechte.value
  if (laufend !== null && fuerProjekt === projectId) return laufend

  fuerProjekt = projectId
  laufend = (async () => {
    try {
      const antwort = await api.module(projectId)
      rechte.value = antwort.meine
    } catch {
      /* Netz weg oder Endpunkt zu: lieber zu wenig anbieten als zu viel. */
      rechte.value = VORSICHTIG
    } finally {
      laufend = null
    }
    return rechte.value ?? VORSICHTIG
  })()
  return laufend
}

/**
 * Hat dieses Projekt überhaupt Kataloge?
 *
 * Steht hier neben den Rechten, weil beides dieselbe Frage beantwortet: wo ein
 * Kundenzugang nach dem Anmelden landet. Einmal je Projekt gefragt und
 * gemerkt; bleibt die Antwort aus, ist der Editor die sichere Wahl, denn den
 * gibt es in jedem Projekt.
 */
const kataloge = new Map<string, boolean>()

async function hatKataloge(projectId: string): Promise<boolean> {
  const gemerkt = kataloge.get(projectId)
  if (gemerkt !== undefined) return gemerkt
  let vorhanden = false
  try {
    vorhanden = (await api.listCollections(projectId)).collections.length > 0
  } catch {
    vorhanden = false
  }
  kataloge.set(projectId, vorhanden)
  return vorhanden
}

function zuruecksetzen(): void {
  rechte.value = null
  fuerProjekt = null
  laufend = null
  kataloge.clear()
}

export interface KundenRechte {
  rechte: DeepReadonly<Ref<string[] | null>>
  darf: (recht: string) => boolean
  laden: (projectId: string) => Promise<string[]>
  hatKataloge: (projectId: string) => Promise<boolean>
  zuruecksetzen: () => void
}

export function useKundenRechte(): KundenRechte {
  return {
    rechte: readonly(rechte),
    darf: (recht: string) => (rechte.value ?? VORSICHTIG).includes(recht),
    laden,
    hatKataloge,
    zuruecksetzen,
  }
}
